function focusRing(color = 'transparent') {
  // If the color is transparent, we don't want to show the white ring
  const innerRing = `0px 0px 0px 2px ${color === 'transparent' ? 'transparent' : 'white'}`
  const outerRing = `0px 0px 0px 4px ${color}`
  return `${innerRing}, ${outerRing}`
}

const baseStyleControl = {
  borderWidth: '1px',
  borderColor: 'var(--vndly-color-input-border)',
  borderRadius: 'base',
  bg: 'var(--vndly-color-input-background)',
  boxShadow: focusRing('transparent'),

  // Hover Gray Circle
  '&:not([data-disabled], [data-readonly])': {
    _after: {
      content: "''",
      boxShadow: '0 0 0 7px var(--vndly-color-input-shadow-hover)',
      position: 'absolute',
      borderRadius: '9999px',
      width: '24px',
      height: '24px',
      zIndex: '-1',
      transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
      opacity: 0,
      transform: 'scale(0.7)'
    },
    _hover: {
      _after: {
        opacity: 1,
        transform: 'scale(1)'
      }
    }
  },

  _hover: {
    '&:not([data-checked], [data-invalid], [data-warned], [data-readonly], [data-disabled])': {
      borderWidth: '1px',
      borderColor: 'var(--vndly-color-input-border-hover)',
      _disabled: {
        bg: 'var(--vndly-color-input-background-disabled)',
        borderColor: 'var(--vndly-color-input-border-disabled)',
        color: 'var(--vndly-color-input)'
      }
    }
  },

  _checked: {
    borderColor: 'var(--vndly-color-input-border-checked)',
    bg: 'var(--vndly-color-input-background-checked)',
    color: 'var(--vndly-color-input-icon-checked)',
    _disabled: {
      borderColor: 'var(--vndly-color-input-border-checked-disabled)',
      bg: 'var(--vndly-color-input-background-checked-disabled)',
      color: 'var(--vndly-color-input-icon-checked)',
      cursor: 'not-allowed',
      _hover: {
        borderColor: 'var(--vndly-color-input-border-checked-disabled)',
        bg: 'var(--vndly-color-input-background-checked-disabled)',
        color: 'var(--vndly-color-input-icon-checked)'
      },
      _active: {
        borderColor: 'var(--vndly-color-input-border-checked-disabled)',
        bg: 'var(--vndly-color-input-background-checked-disabled)',
        color: 'var(--vndly-color-input-icon-checked)'
      }
    },
    '&:not([data-readonly], [data-disabled])': {
      _hover: {
        borderColor: 'var(--vndly-color-input-border-checked)',
        bg: 'var(--vndly-color-input-background-checked)',
        color: 'var(--vndly-color-input-icon-checked)'
      }
    },
    _focusVisible: {
      boxShadow: focusRing('var(--vndly-color-input-shadow-focused)'),
      _hover: {
        boxShadow: focusRing('var(--vndly-color-input-shadow-focused)')
      }
    },
    _active: {
      borderColor: 'var(--vndly-color-input-border-checked)',
      bg: 'var(--vndly-color-input-background-checked)',
      color: 'var(--vndly-color-input-icon-checked)'
    }
  },

  _indeterminate: {
    bg: 'var(--vndly-color-input-background-indeterminate)',
    borderColor: 'var(--vndly-color-input-border-indeterminate)',
    color: 'var(--vndly-color-input-icon-indeterminate)',
    _disabled: {
      borderColor: 'var(--vndly-color-input-border-indeterminate-disabled)',
      bg: 'var(--vndly-color-input-background-indeterminate-disabled)'
    },
    '&:not([data-readonly])': {
      _hover: {
        bg: 'var(--vndly-color-input-background-indeterminate)',
        borderColor: 'var(--vndly-color-input-border-indeterminate)',
        color: 'var(--vndly-color-input-icon-indeterminate)'
      }
    },
    _focusVisible: {
      boxShadow: focusRing('var(--vndly-color-input-border-focused)')
    },
    '& > svg': {
      opacity: 1
    }
  },

  _disabled: {
    bg: 'var(--vndly-color-input-background-disabled)',
    borderColor: 'var(--vndly-color-input-border-disabled)',
    color: 'var(--vndly-color-input-icon)',
    cursor: 'not-allowed',
    _hover: {
      bg: 'var(--vndly-color-input-background-disabled)',
      borderColor: 'var(--vndly-color-input-border-disabled)',
      color: 'var(--vndly-color-input-icon)',
      boxShadow: focusRing('transparent')
    },
    _active: {
      bg: 'var(--vndly-color-input-background-disabled)',
      borderColor: 'var(--vndly-color-input-border-disabled)',
      color: 'var(--vndly-color-input-icon)'
    }
  },

  _readOnly: {
    cursor: 'default',
    '&:not([data-checked])': {
      bg: 'var(--vndly-color-input-background-disabled)',
      _hover: {
        bg: 'var(--vndly-color-input-background-disabled)',
        borderColor: 'var(--vndly-color-input-border)'
      }
    },
    _checked: {
      _hover: {
        bg: 'var(--vndly-color-input-background-checked)',
        borderColor: 'var(--vndly-color-input-border-checked)',
        color: 'var(--vndly-color-input-icon-checked)'
      }
    },
    _indeterminate: {
      _hover: {
        bg: 'var(--vndly-color-input-background-indeterminate)',
        borderColor: 'var(--vndly-color-input-border-indeterminate)',
        color: 'var(--vndly-color-input-icon-indeterminate)'
      }
    }
  },

  _focusVisible: {
    '&:not([data-checked], [data-invalid], [data-warned])': {
      borderWidth: '2px',
      borderColor: 'var(--vndly-color-input-border-focused)',
      boxShadow: focusRing('transparent')
    }
  },

  _active: {
    '&:not([data-focus], [data-checked], [data-invalid], [data-warned])': {
      borderColor: 'var(--vndly-color-input-border-active)',
      borderWidth: '1px'
    }
  },

  _invalid: makeInvalidControlStyles('invalid'),

  // warned styles
  'label[data-warned]:not([data-invalid]) &': makeInvalidControlStyles('warned')
}

const baseStyleLabel = {
  'label:has(input[readonly]) > &': {
    cursor: 'text',
    userSelect: 'text'
  }
}

const baseStyleContainer = {
  '&:has(input[readonly])': {
    cursor: 'default'
  }
}

function makeInvalidControlStyles(cssVarVariant) {
  return {
    borderWidth: '2px',
    borderColor: `var(--vndly-color-input-border-${cssVarVariant})`,
    _disabled: {
      bg: 'var(--vndly-color-input-background-disabled)',
      color: 'var(--vndly-color-input)',
      borderColor: `var(--vndly-color-input-border-disabled)`
    },
    _focusVisible: {
      boxShadow: focusRing('var(--vndly-color-input-shadow-focused)'),
      _hover: {
        boxShadow: focusRing('var(--vndly-color-input-shadow-focused)')
      }
    },
    _checked: {
      borderColor: 'brand.primary.base',
      _disabled: {
        borderColor: 'var(--vndly-color-input-border-checked-disabled)',
        bg: 'var(--vndly-color-input-background-checked-disabled)',
        color: 'var(--vndly-color-input-icon-checked)',
        cursor: 'not-allowed'
      },
      boxShadow: focusRing(`var(--vndly-color-input-shadow-${cssVarVariant})`),
      _hover: {
        boxShadow: focusRing(`var(--vndly-color-input-shadow-${cssVarVariant})`)
      },
      _focusVisible: {
        boxShadow: focusRing('var(--vndly-color-input-shadow-focused)'),
        _hover: {
          boxShadow: focusRing('var(--vndly-color-input-shadow-focused)')
        }
      }
    }
  }
}

const variantIcon = props => ({
  label: {
    position: 'relative',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'top',
    transition: 'all 0.15s ease-out',
    border: '2px solid',
    borderColor: 'transparent',
    borderRadius: 'base',
    p: '2px',
    m: -1,
    _disabled: { cursor: 'not-allowed' },
    _hover: { bgColor: 'gray.200' },
    _focusVisible: { borderColor: 'var(--vndly-color-container-border-focused)' }
  },
  customControl: {
    color: 'var(--vndly-color-container-icon)',
    transition: 'all 0.15s ease-out',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'top',
    userSelect: 'none',
    flexShrink: 0,
    _checked: { color: props.checkedColor ?? 'var(--vndly-color-container-icon)' },
    _hover: {
      color: props.isChecked
        ? props.checkedColor ?? 'var(--vndly-color-container-border-focused)'
        : 'var(--vndly-color-container-border-focused)'
    },
    _focusVisible: {
      color: props.isChecked
        ? props.checkedColor ?? 'var(--vndly-color-container-border-focused)'
        : 'var(--vndly-color-container-border-focused)'
    }
  }
})

export const Checkbox = {
  baseStyle: {
    control: baseStyleControl,
    label: baseStyleLabel,
    container: baseStyleContainer
  },
  sizes: {
    lg: {
      label: { fontSize: 'md' }
    },
    xl: {
      control: { w: 6, h: 6 },
      label: { fontSize: 'md' }
    }
  },
  defaultProps: {
    size: 'xl'
  },
  variants: {
    icon: variantIcon
  }
}
